/**
 * Version Operations Hook
 * CRUD operations and mutations for contract versions
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import { toast } from 'sonner';
import { isInvoiceCollisionError } from '@/utils/contractErrorMessages';
import { extractBackendError, translateBackendError } from '@/lib/translateBackendError';
import type {
  billing_ScheduleChangeRequest,
  billing_VersionSplitResponse,
} from '@/api';

// Query keys for contract versions
export const contractVersionKeys = {
  all: ['contract-versions'] as const,
  group: (groupId: string) => [...contractVersionKeys.all, 'group', groupId] as const,
  activeVersions: (contractId: string, start?: string, end?: string) =>
    [...contractVersionKeys.all, 'active', contractId, { start, end }] as const,
};

/**
 * Hook to schedule a contract change
 */
export function useScheduleContractChange() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async (params: {
      contractId: string;
      request: billing_ScheduleChangeRequest;
      /** No success toast: useCancelAndProceed re-runs the change and reports once. */
      silent?: boolean;
    }): Promise<billing_VersionSplitResponse> => {
      const { ContractsService } = await import('@/api');
      return await ContractsService.postApiV1ContractsScheduleChange(params.contractId, params.request);
    },
    onSuccess: (data, variables) => {
      // Invalidate contract versions for the group
      if (data.contract_group_id) {
        queryClient.invalidateQueries({
          queryKey: contractVersionKeys.group(data.contract_group_id)
        });
      }

      // Invalidate active versions for the contract
      queryClient.invalidateQueries({
        queryKey: contractVersionKeys.activeVersions(variables.contractId)
      });

      // Invalidate all contract queries to refresh contract status
      queryClient.invalidateQueries({ queryKey: ['contracts'] });

      if (!variables.silent) {
        toast.success(
          i18n._(
            msg`Contract change scheduled successfully for ${data.active_from ?? ''}`,
          )
        );
      }
    },
    onError: (error: unknown) => {
      // Invoice-collision 422s are surfaced by the BlockingInvoicesDialog in the
      // component; suppress the toast here (the rejection still propagates).
      if (isInvoiceCollisionError(error)) {
        return;
      }
      console.error('Schedule contract change error:', error);
      // No raw backend message / "Unknown error" dead-end — a coded rejection
      // says which version to select; anything else gets a plain next step.
      toast.error(
        translateBackendError(
          extractBackendError(error).code,
          i18n,
          msg`Couldn't schedule this change. Please check the dates and try again.`,
        ),
      );
    },
  });
}

/**
 * Utility hook for version management operations
 */
export function useVersionOperations() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  const invalidateVersions = (groupId?: string) => {
    if (groupId) {
      queryClient.invalidateQueries({
        queryKey: contractVersionKeys.group(groupId)
      });
    }
    queryClient.invalidateQueries({ queryKey: contractVersionKeys.all });
    queryClient.invalidateQueries({ queryKey: ['contracts'] });
  };

  const refreshVersionData = async (groupId?: string) => {
    if (groupId) {
      await queryClient.refetchQueries({
        queryKey: contractVersionKeys.group(groupId)
      });
    }
    toast.success(i18n._(msg`Version data refreshed`));
  };

  return {
    invalidateVersions,
    refreshVersionData,
  };
}